import { Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { RoleType, useCurrentUser } from '@feature/security';
import { HierarchyDisciplineSelect, HierarchyFacultySelect } from '@feature/utils';
import { HierarchyDepartmentSelect } from '@feature/utils/components/HierarchyDepartmentSelect';
import {
  HierarchySelection,
  useDepartmentAdminHierarchy,
  useDisciplineAdminHierarchy,
  useFacultyAdminHierarchy,
  useHierarchySelection,
  useUniversityAdminHierarchy,
} from '@feature/utils/components/hooks/hierarchy/useHierarchy';

const EMPTY_SELECTION: HierarchySelection = {
  facultyId: null,
  departmentId: null,
  disciplineId: null,
  userId: null,
};

/**
 * The hierarchy selection lives in the url, so it can hold ids left over from another screen or
 * from a role that had wider access. Only the ids the current role's pickers actually control are
 * kept, the rest would either be ignored or rejected with a 403 by the server.
 */
export function useWosIsiOrgSelection(): HierarchySelection {
  const { currentRole } = useCurrentUser();
  const selection = useHierarchySelection();

  if (
    currentRole === RoleType.ADMIN ||
    currentRole === RoleType.UNIVERSITY_ADMIN ||
    currentRole === RoleType.FACULTY_ADMIN
  ) {
    return selection;
  }

  if (currentRole === RoleType.DEPARTMENT_ADMIN) {
    return { ...selection, facultyId: null };
  }

  if (currentRole === RoleType.DISCIPLINE_ADMIN) {
    return { ...selection, facultyId: null, departmentId: null };
  }

  return EMPTY_SELECTION;
}

function UniversityOrgFilter() {
  const { selectProps } = useUniversityAdminHierarchy();
  return <HierarchyFacultySelect {...selectProps} />;
}

function FacultyOrgFilter() {
  const { selectProps } = useFacultyAdminHierarchy();
  return <HierarchyFacultySelect {...selectProps} />;
}

function DepartmentOrgFilter() {
  const { selectProps } = useDepartmentAdminHierarchy();
  return <HierarchyDepartmentSelect {...selectProps} />;
}

function DisciplineOrgFilter() {
  const { selectProps } = useDisciplineAdminHierarchy();
  return <HierarchyDisciplineSelect {...selectProps} />;
}

/**
 * Renders only the org pickers the current role is allowed to use. The selection itself lives
 * in the url search params, read back with `useHierarchySelection`.
 */
export function WosIsiReportOrgFilter() {
  const { currentRole } = useCurrentUser();
  const { t } = useTranslation();

  if (currentRole === RoleType.ADMIN || currentRole === RoleType.UNIVERSITY_ADMIN) {
    return <UniversityOrgFilter />;
  }

  if (currentRole === RoleType.FACULTY_ADMIN) {
    return <FacultyOrgFilter />;
  }

  if (currentRole === RoleType.DEPARTMENT_ADMIN) {
    return <DepartmentOrgFilter />;
  }

  if (currentRole === RoleType.DISCIPLINE_ADMIN) {
    return <DisciplineOrgFilter />;
  }

  return (
    <Text
      size="xs"
      c="dimmed"
    >
      {t('wosIsiReport.ownScopeOnly')}
    </Text>
  );
}
